import { EditActions } from '@/components';
import { useEditableDetail } from '@/hooks';
import { cn, mergeRefs, NOOP } from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { Input as InputAntd } from 'antd';

import type { InputProps, InputRef } from 'antd';

export interface IInputProps extends InputProps {
	label?: string;
	labelClassName?: string;
	inputClassName?: string;
	textEditClassName?: string;
	containerClassName?: string;
	require?: boolean;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	horizontal?: boolean;
	inputPassword?: boolean;
	inputRef?: React.RefObject<InputRef>;
	isValidValue?: boolean | undefined;
	formatInput?: (value: string, number?: number) => void;
	isDetail?: boolean;
	emptyValue?: string;
	handleOnSave?: () => void;
	unit?: string;
	isUploadFileName?: boolean;
}
const Input = React.forwardRef<HTMLInputElement, IInputProps>(
	(
		{
			label,
			labelClassName = '',
			require,
			inputClassName = '',
			textEditClassName = '',
			touched,
			errorCode,
			hasServerError,
			horizontal = true,
			containerClassName = '',
			inputPassword,
			inputRef,
			isValidValue,
			suffix,
			formatInput,
			isDetail = false,
			emptyValue = '-',
			handleOnSave = undefined,
			unit,
			isUploadFileName,
			...rest
		},
		ref
	) => {
		const {
			formik,
			isUpdate,
			editable,
			handleLabelClick,
			onCancel,
			onSave,
			setEditable,
		} = useEditableDetail(
			{ fieldName: rest.name as string, handleOnSave },
			ref
		);
		const InputComponent = inputPassword ? InputAntd.Password : InputAntd;

		const _value = useMemo(() => {
			if (isUploadFileName && rest.value) {
				return (rest.value as string).split('.').slice(0, -1).join('.');
			}
			return rest.value;
		}, [isUploadFileName, rest.value]);

		const _onPressEnter = useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				rest?.onPressEnter?.(e);

				const fieldName = rest.name || '';
				const isValueUnchanged =
					formik?.initialValues?.[fieldName] === formik?.values?.[fieldName];

				if (isValueUnchanged && isUpdate && isDetail) {
					setEditable(false);
				}
			},
			[
				formik?.initialValues,
				formik?.values,
				rest?.onPressEnter,
				rest.name,
				isUpdate,
				isDetail,
			]
		);

		const iconSuffix =
			suffix ||
			(isValidValue === undefined ? undefined : isValidValue ? (
				<CheckCircleFilled className="text-green1" />
			) : (
				<CloseCircleFilled className="text-red7" />
			));

		const innerInputRef = useRef<InputRef>(null);

		const mergedRef = mergeRefs(inputRef, innerInputRef);

		useEffect(() => {
			if (editable && isUpdate && isDetail) {
				innerInputRef?.current?.focus();
			}
		}, [editable]);

		return (
			<div
				className={cn(
					'flex flex-1 w-full gap-[1.667vw] xl:gap-2',
					horizontal ? 'items-center' : 'flex-col',
					containerClassName,
					isMobile && 'gap-[8px]'
				)}
			>
				{label && (
					<label
						className={cn(
							'text-[13px] font-medium text-font2 min-w-[110px]',
							labelClassName
						)}
					>
						{label} {require && <span className="text-red6">*</span>}
					</label>
				)}

				{isDetail && !editable ? (
					<div className="flex w-full">
						<div
							className={cn(
								`${unit ? 'flex' : 'block'} break-all box-border w-full text-[13px] xl:text-[13px] xl:h-[30px] text-font2 py-[5px] xl:px-[11px] text-left overflow-hidden xl:hover:border xl:hover:border-gray1 cursor-text truncate whitespace-pre`,
								rest.disabled &&
									'cursor-not-allowed hover:border-none hover:bg-porcelain',
								textEditClassName
							)}
							onClick={!rest.disabled ? handleLabelClick : undefined}
							title={rest.value?.toString() || emptyValue}
						>
							{rest.value?.toString() || emptyValue}
							{unit && (
								<p className="ps-1 text-[13px] my-auto w-[50px]">{unit}</p>
							)}
						</div>
					</div>
				) : (
					<div className="flex items-center gap-2 w-full">
						<InputComponent
							{...rest}
							value={_value}
							autoComplete="new-password"
							size="large"
							ref={mergedRef}
							className={cn(
								'bg-white px-[11px] py-[7px] w-full text-[13px] h-[40px] xl:h-[30px] text-font2 xl:rounded-none',
								inputClassName
							)}
							status={
								(touched && errorCode) || hasServerError ? 'error' : undefined
							}
							suffix={iconSuffix}
							onBlur={() => {
								isMobile && window.scrollTo(0, 0);
								isDetail ? NOOP : rest.onBlur;
							}}
							onPressEnter={_onPressEnter}
						/>
						{isUpdate && isDetail && (
							<EditActions onCancel={onCancel} onSave={onSave} />
						)}
					</div>
				)}
			</div>
		);
	}
);

Input.displayName = 'Input';

export default memo(Input);
